import * as React from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { EmailTemplate } from "../../types/emailTemplate";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useGetEmailTemplateByIdQuery,
  useEditEmailTemplateMutation,
} from "../../services/email-template.service";
import { toast } from "react-toastify";
import { CardBase, StyledTextField, Button } from "../../components/global";

const typeOptions = [
  { value: "{first_name}", label: "{first_name}" },
  { value: "{last_name}", label: "{last_name}" },
  { value: "{email}", label: "{email}" },
  { value: "{project_name}", label: "{project_name}" },
  { value: "{company_name}", label: "{company_name}" },
  { value: "{rate_per_hour}", label: "{rate_per_hour}" },
  { value: "{max_hour}", label: "{max_hour}" },
  { value: "{fixed_price}", label: "{fixed_price}" },
  { value: "{app_name}", label: "{app_name}" },
  { value: "{logo}", label: "{logo}" },
];

interface EmailTemplateEditPageProps {
  data: EmailTemplate | null;
}

const EmailTemplateEditPage = ({ data }: EmailTemplateEditPageProps) => {
  const {
    data: emailTemplate,
    isLoading,
    isFetching,
  } = useGetEmailTemplateByIdQuery(data?.id!, {
    skip: !data?.id,
  });

  const [cur, setCur] = React.useState<number>(0);

  const [edit, { isLoading: editing }] = useEditEmailTemplateMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    setFocus,
    getValues,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (data) {
        payload.id = data?.id!;
        await edit(payload).unwrap();
        clearErrors();
        toast.success(`Template '${data?.title}' updated`);
      }
    } catch (err) { }
  };

  React.useEffect(() => {
    if (emailTemplate) {
      setValue("subject", emailTemplate?.subject || "");
      setValue("template", emailTemplate?.template || "");
      clearErrors();
    }
  }, [emailTemplate, setValue, clearErrors]);

  const handleInsertTextOnTemplate = (option: string) => {
    const currentStr = getValues("template");

    setValue(
      "template",
      currentStr.slice(0, cur) + option + currentStr.slice(cur)
    );
  };

  if (!data) {
    return (
      <CardBase
        title="Select a Template"
        useGlassCard={false}
        sx={{ minHeight: "400px" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
            color: "text.secondary",
          }}
        >
          Please select a template from the list
        </Box>
      </CardBase>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardBase
        title={data?.title}
        useGlassCard={false}
        sx={{ mb: 2 }}
      >
        {(isLoading || isFetching) && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <Stack spacing={2}>
          <Controller
            name="subject"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <StyledTextField
                label="Subject"
                value={value || ""}
                fullWidth
                onChange={onChange}
                onBlur={onBlur}
                inputRef={ref}
                error={!!errors.subject}
                helperText={!!errors.subject && "This field is required"}
              />
            )}
          />
          <Box>
            <Box sx={{ mb: 1, fontSize: "0.875rem", color: "text.secondary", fontWeight: 500 }}>
              Available Variables:
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {typeOptions.map((item, index) => (
                <Chip
                  key={index}
                  label={item.label}
                  onClick={() => {
                    setTimeout(() => {
                      handleInsertTextOnTemplate(`${item.value}`);
                      setFocus("template");
                    }, 100);
                  }}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: "rgba(244, 162, 89, 0.15)",
                    color: "#F4A259",
                    fontWeight: 600,
                    border: "1.5px solid rgba(244, 162, 89, 0.4)",
                    boxShadow: "0 1px 3px rgba(244, 162, 89, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(244, 162, 89, 0.25)",
                      borderColor: "#F4A259",
                      boxShadow: "0 2px 6px rgba(244, 162, 89, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                />
              ))}
            </Stack>
          </Box>
          <Controller
            name="template"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <StyledTextField
                label="Template"
                value={value || ""}
                fullWidth
                multiline
                rows={12}
                onChange={(e) => {
                  setCur(e.target.selectionStart || 0);
                  setValue("template", e.target.value, {
                    shouldValidate: true,
                  });
                  setCur(e.target.selectionStart || 0);
                  onChange(e);
                }}
                onBlur={(e) => {
                  setCur(e.target.selectionStart || 0);
                  onBlur();
                }}
                onSelect={(e: any) => {
                  setCur(e.target.selectionStart || 0);
                }}
                inputRef={ref}
                error={!!errors.template}
                helperText={!!errors.template && "This field is required"}
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  },
                }}
              />
            )}
          />
        </Stack>
      </CardBase>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={editing}
          sx={{
            backgroundColor: "#F4A259",
            color: "#ffffff",
            fontWeight: 600,
            boxShadow: "0 2px 8px rgba(244, 162, 89, 0.3)",
            "&:hover": {
              backgroundColor: "#E8944A",
              boxShadow: "0 4px 12px rgba(244, 162, 89, 0.4)",
            },
          }}
        >
          Update Template
        </LoadingButton>
      </Stack>
    </form>
  );
};
export default EmailTemplateEditPage;
